import { isTextModel, modelKind } from './modelKinds';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { modelPolicyPath } from './workflowUiState';
import { useQuery } from '@tanstack/react-query';
import {
  Sparkles,
  ArrowUp,
  ArrowUpRight,
  ArrowRight,
  Plus,
  ChevronRight,
  Clapperboard,
  UsersRound,
  BookOpen,
  Brain,
  GitBranch,
  Check,
  Clock3,
  Film,
  CirclePlay,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Link2,
  CircleHelp,
  Wallet,
  History,
  Save,
  PencilLine,
  Layers3,
  MessageSquare,
  CheckCheck,
} from 'lucide-react';
import { api, type Schema, type Page, labels, formatTime, ApiError } from '../api/client';
import { useStudio } from './App';
import {
  ModelSetupButton,
  ProductionTaskList,
  ContinuityLedger,
  ConnectionEditor,
  useTasks,
} from './production';
import { DesignButton } from './DesignAssistant';
import { CharacterImagesPanel } from './CharacterImages';
import { CharacterVoicePanel } from './CharacterVoices';
const lines = (value: string) =>
  value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
import { useApi, Dialog, Empty, Field, Badge, Spinner, MoonScene } from './shared';

type Version = Schema<'ArtifactVersion'>;
type Artifact = Schema<'Artifact'>;
type Model = Schema<'ModelSummary'>;
const heading = (eyebrow: string, title: string, text: string, action?: ReactNode) => (
  <div className="page-heading">
    <div>
      <span className="eyebrow">{eyebrow}</span>
      <h1>{title}</h1>
      <p>{text}</p>
    </div>
    {action}
  </div>
);
function useArtifacts(kind?: string) {
  const { base, project } = useStudio();
  return useApi<Page<Artifact>>(
    `${base}/artifacts?seriesId=${project?.id ?? ''}${kind ? `&kind=${kind}` : ''}`,
    !!project,
  );
}
async function saveVersion(
  base: string,
  seriesId: string,
  branchId: string,
  kind: Artifact['kind'],
  title: string,
  content: Record<string, unknown>,
  existing?: Artifact,
  characterId?: string,
  episodeId?: string,
) {
  const artifact = existing
    ? await api<Artifact>(`${base}/artifacts/${existing.id}`)
    : await api<Artifact>(`${base}/artifacts`, 'POST', {
        seriesId,
        kind,
        title,
        ...(characterId ? { characterId } : {}),
        ...(episodeId ? { episodeId } : {}),
      });
  const versions = await api<Page<Version>>(`${base}/artifacts/${artifact.id}/versions`);
  const last = versions.items.find((v) => v.branchId === branchId);
  const dependencies = new Set<string>();
  const collect = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }
    if (!value || typeof value !== 'object') return;
    Object.entries(value).forEach(([key, item]) => {
      if ((key === 'versionId' || key.endsWith('VersionId')) && typeof item === 'string')
        dependencies.add(item);
      else if (key.endsWith('VersionIds') && Array.isArray(item))
        item.forEach((id) => {
          if (typeof id === 'string') dependencies.add(id);
        });
      else collect(item);
    });
  };
  collect(content);
  return api<Version>(`${base}/artifacts/${artifact.id}/versions`, 'POST', {
    expectedRevision: artifact.revision,
    branchId,
    schemaVersion: 1,
    content,
    parentVersionIds: last ? [last.id] : [],
    dependencyVersionIds: [...dependencies],
  } satisfies Schema<'ArtifactVersionCreate'>);
}
function modelFailureLabel(code?: string, partial = false) {
  const reasons: Record<string, string> = {
    UPSTREAM_AUTH_EXPIRED: '网关凭据失效或无访问权限，请到模型与连接检查配置。',
    UPSTREAM_RATE_LIMITED: '网关额度或并发暂时受限。',
    UPSTREAM_TIMEOUT: '模型响应超时。已收到的内容会保留，没有自动重发。',
    UPSTREAM_ERROR: '网关返回错误，请查看任务记录后核对连接。',
    AUTHORIZATION_OR_MODEL_CHANGED: '发送前授权或模型配置发生变化，请重新选择模型和授权。',
    OUTCOME_UNKNOWN: '连接中断，结果待核对，没有自动重复调用。',
  };
  return (
    (code && reasons[code]) ||
    (partial ? '回复未完整结束；已收到的正文已保存。' : '本次回复失败，可在任务详情中查看原因。')
  );
}
export function StudioPage() {
  const { project, base, branch, episodes, go, toast } = useStudio();
  const artifacts = useArtifacts();
  const modelQuery = useApi<Page<Model>>(`${base}/models?role=chat`);
  const modelPolicy = useApi<Schema<'ModelPolicy'>>(
    `${base}${project ? `/series/${project.id}` : ''}/model-policy`,
  );
  const models = modelQuery.data?.items ?? [];
  const [input, setInput] = useState(''),
    [modelId, setModelId] = useState(''),
    [sending, setSending] = useState(false);
  const conversations = useApi<Page<Schema<'Conversation'>>>(
    `${base}/conversations?seriesId=${project?.id ?? ''}`,
    !!project,
  );
  const currentEpisode =
    episodes.find((e) => !e.currentReleaseId && !e.productionReleaseId) ??
    episodes[episodes.length - 1];
  const conversation = conversations.data?.items.find(
    (c) => c.scope.branchId === branch?.id && c.scope.episodeId === currentEpisode?.id,
  );
  const messagePath = `${base}/conversations/${conversation?.id ?? ''}/messages`;
  const messages = useQuery<Page<Schema<'Message'>>>({
    queryKey: ['api', messagePath],
    queryFn: () => api<Page<Schema<'Message'>>>(messagePath),
    enabled: !!conversation,
    refetchInterval: (q) =>
      q.state.data?.items.some((m) => ['accepted', 'streaming'].includes(m.status)) ? 1000 : false,
  });
  const jobs = useApi<Page<Schema<'Job'>>>(`${base}/jobs?seriesId=${project?.id ?? ''}`, !!project);
  const chosen =
    models.find((m) => m.catalogModelId === modelId) ??
    models.find(
      (m) =>
        m.selectable &&
        m.catalogModelId === modelPolicy.data?.bindings.chat?.primary.catalogModelId,
    ) ??
    models.find((m) => m.selectable) ??
    models[0];
  useEffect(() => {
    if (project) setInput(sessionStorage.getItem(`dream.draft.${project.id}`) ?? '');
  }, [project?.id]);
  const changeInput = (value: string) => {
    setInput(value);
    if (project) sessionStorage.setItem(`dream.draft.${project.id}`, value);
  };
  if (!project || !branch)
    return (
      <div className="page">
        <Empty
          title="先开启一个故事"
          text="创建系列后，剧情、角色和每集制作会聚在这里。"
          action={
            <button className="button primary" onClick={() => go('projects')}>
              查看系列 <ArrowRight size={16} />
            </button>
          }
        />
      </div>
    );
  const items = artifacts.data?.items ?? [];
  const stage = [
    {
      name: '故事与世界观',
      key: 'story',
      done: items.some((a) => a.kind === 'story_bible'),
      text: '一句话故事、规则与主线',
    },
    {
      name: '人物设定',
      key: 'characters',
      done: items.some((a) => a.kind === 'character_profile'),
      text: '动机、秘密与成长计划',
    },
    {
      name: '本集简报与节奏',
      key: 'episodes',
      done: items.some((a) => a.kind === 'episode_brief'),
      text: '开场、推进、悬念与时长',
    },
    {
      name: '镜头、声音与成片',
      key: 'production',
      done: items.some((a) => a.kind === 'review_cut'),
      text: '分镜、配音、素材与成片导出',
      comingSoon: false,
    },
  ];
  return (
    <div className="studio-layout">
      <section className="conversation-panel">
        <div className="panel-heading">
          <span>
            <Sparkles size={16} /> 创作伙伴
          </span>
          <Badge tone="green">记忆来自项目</Badge>
        </div>
        <div className="conversation-body">
          <div className="assistant-mark">✳</div>
          <span className="eyebrow">LET'S MAKE SOMETHING WORTH WATCHING</span>
          <h2>今天，故事往哪里走？</h2>
          <p className="welcome-copy">
            我们正在创作《{project.title}》。
            <br />
            可以先聊一个灵感，也可以继续完善人物与下一集。
          </p>
          <div className="conversation-context">
            <BookOpen size={16} />
            <div>
              <strong>当前创作上下文</strong>
              <p>
                {branch.name} · {currentEpisode ? `第 ${currentEpisode.number} 集` : '全剧设定'} ·
                约 {project.targetFormat.targetSeconds} 秒 / 集
              </p>
            </div>
            <Check size={14} />
          </div>
          {!messages.data?.items.length && (
            <div className="prompt-suggestions">
              {[
                '帮我整理这个故事的核心冲突',
                '我们先聊聊主角是什么样的人',
                '一起规划第一集的开场',
              ].map((text) => (
                <button key={text} onClick={() => changeInput(text)}>
                  {text}
                  <ArrowUpRight size={14} />
                </button>
              ))}
            </div>
          )}
          {messages.data?.items.map((m) => (
            <div
              key={m.id}
              className={`message ${m.role}`}
              aria-busy={['accepted', 'streaming'].includes(m.status)}
            >
              <span>
                {m.role === 'user' ? '你' : 'Dream'} {m.status === 'partial' && '· 回复未完成'}{' '}
                {m.status === 'streaming' && '· 正在回复…'}
              </span>
              <p>
                {m.content ||
                  (m.status === 'failed'
                    ? '本次回复未完成，请到任务中心查看原因。'
                    : '任务已保存，正在等待模型回复…')}
              </p>
              {['failed', 'partial'].includes(m.status) && (
                <div className="message-issue" role="status">
                  <small>
                    {modelFailureLabel(
                      jobs.data?.items.find((j) => j.runId === m.runId)?.errorCode,
                      m.status === 'partial',
                    )}
                  </small>
                  <button
                    type="button"
                    className="button small secondary"
                    onClick={() => go('jobs')}
                  >
                    查看任务详情
                  </button>
                </div>
              )}
            </div>
          ))}
          {messages.isError && (
            <div className="info-box">
              暂时无法同步回复。后台请求不会重复提交。
              <button
                type="button"
                className="button small secondary"
                onClick={() => void messages.refetch()}
              >
                重新同步
              </button>
            </div>
          )}
          <div className="conversation-footnote">
            <ShieldCheck size={14} />
            <span>
              讨论中的想法会保留为草稿。
              <br />
              只有你的明确确认，才会成为正式设定。
            </span>
          </div>
        </div>
        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            if (!chosen?.selectable) {
              toast('当前聊天模型尚未通过能力验证。先到“模型与连接”完成配置。', true);
              return;
            }
            setSending(true);
            void (async () => {
              const scope = {
                seriesId: project.id,
                branchId: branch.id,
                artifactVersionIds: [],
                ...(currentEpisode ? { episodeId: currentEpisode.id } : {}),
              };
              const conv =
                conversation ??
                (await api<Schema<'Conversation'>>(`${base}/conversations`, 'POST', {
                  scope,
                  title: '创作讨论',
                }));
              await api(`${base}/conversations/${conv.id}/messages`, 'POST', {
                expectedRevision: conv.revision,
                content: input,
                attachmentMediaIds: [],
                intent: 'discuss',
                scope,
                modelOverride: {
                  catalogModelId: chosen.catalogModelId,
                  modelRevision: chosen.revision,
                  role: 'chat',
                  allowFallback: false,
                },
              } satisfies Schema<'MessageCreate'>);
              changeInput('');
              await conversations.refetch();
              await messages.refetch();
            })()
              .catch((e) => toast((e as Error).message, true))
              .finally(() => setSending(false));
          }}
        >
          <textarea
            aria-label="与创作伙伴讨论"
            placeholder="讲讲你的想法，或告诉我哪里想修改…"
            rows={3}
            value={input}
            onChange={(e) => changeInput(e.target.value)}
          />
          <div className="composer-bottom">
            <select
              aria-label="聊天模型"
              value={chosen?.catalogModelId ?? ''}
              onChange={(e) => setModelId(e.target.value)}
            >
              {[...models]
                .sort((a, b) => Number(b.selectable) - Number(a.selectable))
                .map((m) => (
                  <option key={m.catalogModelId} value={m.catalogModelId} disabled={!m.selectable}>
                    {m.displayName}
                    {m.selectable
                      ? ''
                      : m.blockerReasons.includes('CONNECTION_NOT_CONFIGURED')
                        ? ' · 未配置连接'
                        : ' · 待测试启用'}
                  </option>
                ))}
            </select>
            <button
              className="send-button"
              disabled={
                !input.trim() ||
                sending ||
                messages.data?.items.some((m) => ['accepted', 'streaming'].includes(m.status))
              }
              aria-label="发送讨论"
            >
              {sending ? <Spinner /> : <ArrowUp size={19} />}
            </button>
          </div>
          <small>
            {chosen?.selectable
              ? '模型调用使用工作室已批准的授权额度。'
              : '已发现模型待验证；你的输入会保留，不会提交收费请求。'}
          </small>
        </form>
      </section>
      <section className="creative-canvas">
        <div className="canvas-top">
          <div>
            <span className="eyebrow">STORY IN PROGRESS</span>
            <h1>{project.title}</h1>
          </div>
          <button className="subtle-button" onClick={() => go('episodes')}>
            <GitBranch size={14} />
            {branch.name}
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="canvas-hero">
          <MoonScene small />
          <div className="canvas-hero-overlay">
            <span>连续叙事 / SERIES</span>
            <strong>
              {currentEpisode
                ? `第 ${currentEpisode.number.toString().padStart(2, '0')} 集`
                : '故事，从这里开始'}
            </strong>
            <p>{currentEpisode?.title ?? '先确定世界，再让角色走进来。'}</p>
            <Badge>目标 {formatTime(project.targetFormat.targetSeconds)}</Badge>
          </div>
        </div>
        <div className="section-heading compact">
          <h2>创作进程</h2>
          <span className="quiet">每一步，都留下可继续的版本</span>
        </div>
        <div className="stage-grid">
          {stage.map((st, i) => (
            <button
              key={st.key + i}
              onClick={() => go(st.key)}
              className="stage-card"
              disabled={st.comingSoon}
            >
              <span className="stage-index">0{i + 1}</span>
              <div>
                <h3>{st.name}</h3>
                <p>{st.text}</p>
              </div>
              <span className={`stage-state ${st.done ? 'started' : ''}`}>
                {st.comingSoon ? '开发中' : st.done ? '已建立' : '开始创作'}
              </span>
              <ArrowUpRight size={16} />
            </button>
          ))}
        </div>
        <div className="continuity-strip">
          <Brain size={20} />
          <div>
            <strong>不只是下一段视频，是故事的下一集。</strong>
            <p>人物设定、道具、知情范围和未解伏笔，会随着定稿延续。</p>
          </div>
          <button className="icon-button" aria-label="查看连续性记忆" onClick={() => go('memory')}>
            <ArrowUpRight size={19} />
          </button>
        </div>
      </section>
    </div>
  );
}
export function EpisodesPage() {
  const { project, branch, episodes, base, perform, busy, go } = useStudio();
  const [selected, setSelected] = useState<Schema<'Episode'> | null>(null);
  const briefs = useArtifacts('episode_brief');
  const writtenEpisodeIds = new Set(
    briefs.data?.items.filter((a) => BigInt(a.revision) > 1n).map((a) => a.episodeId),
  );
  if (!project || !branch) return <MissingProject />;
  const create = () =>
    void perform(async () => {
      const all = await api<Page<Schema<'Branch'>>>(`${base}/series/${project.id}/branches`);
      const fresh = all.items.find((b) => b.id === branch.id)!;
      const previous = episodes[episodes.length - 1];
      const number = (previous?.number ?? 0) + 1;
      try {
        const episode = await api<Schema<'Episode'>>(
          `${base}/series/${project.id}/episodes`,
          'POST',
          {
            branchId: branch.id,
            number,
            title: number === 1 ? '第一幕' : `第 ${number} 集`,
            expectedRevision: fresh.revision,
            continuityBaseline: {
              canonRevision: fresh.canonRevision,
              dependencyValidity: previous && !previous.currentReleaseId ? 'provisional' : 'valid',
              ...(previous ? { previousEpisodeId: previous.id, previousBranchId: branch.id } : {}),
              ...(previous?.currentReleaseId
                ? { previousReleaseId: previous.currentReleaseId }
                : {}),
              previousDraftVersionIds: [],
            },
          },
        );
        setSelected(episode);
      } catch (e) {
        if (e instanceof ApiError && e.problem.existingResourceId) {
          setSelected(
            await api<Schema<'Episode'>>(`${base}/episodes/${e.problem.existingResourceId}`),
          );
        } else throw e;
      }
    }, '新剧集已创建');
  return (
    <div className="page">
      {heading(
        'A STORY, ONE MINUTE AT A TIME',
        '让每一集，都接得上。',
        '保留人物和故事的来处，把创作继续往前推进。',
        <button className="button primary" disabled={busy} onClick={create}>
          <Plus size={17} /> {episodes.length ? `继续第 ${episodes.length + 1} 集` : '创建第一集'}
        </button>,
      )}
      <div className="stats-bar">
        <div>
          <b>{episodes.length.toString().padStart(2, '0')}</b>
          <span>已规划剧集</span>
        </div>
        <div>
          <b>{formatTime(project.targetFormat.targetSeconds)}</b>
          <span>单集目标 · ±{project.targetFormat.toleranceSeconds} 秒</span>
        </div>
        <div>
          <b>
            {episodes
              .filter((e) => e.currentReleaseId || e.productionReleaseId)
              .length.toString()
              .padStart(2, '0')}
          </b>
          <span>已发布剧集</span>
        </div>
        <div>
          <GitBranch size={17} />
          <span>
            {branch.name} · 正史版本 {branch.canonRevision}
          </span>
        </div>
      </div>
      {episodes.length === 0 ? (
        <Empty
          icon={<Clapperboard size={30} />}
          title="第一幕，还没开拍"
          text="先写清楚本集的目标、开场和结尾。人物与剧情会随着每集定稿延续。"
          action={
            <button className="button secondary" onClick={create} disabled={busy}>
              创建第一集 <ArrowRight size={16} />
            </button>
          }
        />
      ) : (
        <div className="episode-grid">
          {episodes.map((ep) => (
            <button className="episode-card" key={ep.id} onClick={() => setSelected(ep)}>
              <div className="episode-preview">
                <span className="episode-num">EP.{ep.number.toString().padStart(2, '0')}</span>
                <Clapperboard size={35} />
                <span className="episode-time">{formatTime(ep.targetFormat.targetSeconds)}</span>
              </div>
              <div className="episode-info">
                <div>
                  <h3>{ep.title}</h3>
                  <Badge
                    tone={
                      ep.currentReleaseId || ep.productionReleaseId
                        ? 'green'
                        : ep.continuityBaseline.dependencyValidity === 'provisional'
                          ? 'amber'
                          : 'neutral'
                    }
                  >
                    {ep.currentReleaseId || ep.productionReleaseId
                      ? '已发布'
                      : ep.continuityBaseline.dependencyValidity === 'provisional'
                        ? ep.predecessorProductionReleaseId
                          ? '待确认前集承接'
                          : '预制作 · 等待前集'
                        : writtenEpisodeIds.has(ep.id)
                          ? '简报已保存'
                          : '待编写'}
                  </Badge>
                </div>
                <p>
                  {ep.number === 1
                    ? '系列的第一步，从这里展开。'
                    : `承接第 ${ep.number - 1} 集的人物与剧情。`}
                </p>
                <div className="mini-stages">
                  <span>简报</span>
                  <i />
                  <span>剧本</span>
                  <i />
                  <span>分镜</span>
                  <i />
                  <span>成片</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="tip-line">
        <ShieldCheck size={16} /> 上一集尚未定稿时，可以先预制作；交付前会复核实际承接变化。
      </div>
      {selected && <EpisodeEditor episode={selected} close={() => setSelected(null)} />}
    </div>
  );
}
function EpisodeEditor({ episode, close }: { episode: Schema<'Episode'>; close: () => void }) {
  const { base, project, branch, refresh, toast } = useStudio();
  const artifacts = useArtifacts('episode_brief');
  const existing = artifacts.data?.items.find((a) => a.episodeId === episode.id);
  const versions = useApi<Page<Version>>(
    `${base}/artifacts/${existing?.id ?? ''}/versions`,
    !!existing,
  );
  const latest = versions.data?.items.find((v) => v.branchId === branch?.id);
  const [goal, setGoal] = useState(''),
    [opening, setOpening] = useState(''),
    [ending, setEnding] = useState(''),
    [changes, setChanges] = useState(''),
    [questions, setQuestions] = useState(''),
    [extra, setExtra] = useState<Record<string, unknown>>({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    if (latest) {
      setGoal(String(latest.content.goal ?? ''));
      setOpening(String(latest.content.openingHook ?? ''));
      setEnding(String(latest.content.endingHook ?? ''));
      setChanges(((latest.content.plannedStateChanges as string[]) ?? []).join('\n'));
      setQuestions(((latest.content.unresolvedQuestions as string[]) ?? []).join('\n'));
      setExtra(latest.content);
    }
  }, [latest?.id]);
  return (
    <Dialog
      title={`第 ${episode.number} 集 · ${episode.title}`}
      subtitle={`目标 ${formatTime(episode.targetFormat.targetSeconds)} · 简报先决定这一集为什么值得看`}
      close={close}
    >
      <form
        className="dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          if (!project || !branch) return;
          setBusy(true);
          setError('');
          void saveVersion(
            base,
            project.id,
            branch.id,
            'episode_brief',
            `第 ${episode.number} 集简报`,
            {
              ...extra,
              episodeId: episode.id,
              goal,
              openingHook: opening,
              endingHook: ending,
              targetSeconds: episode.targetFormat.targetSeconds,
              plannedStateChanges: lines(changes),
              unresolvedQuestions: lines(questions),
            },
            existing,
            undefined,
            episode.id,
          )
            .then(async () => {
              await refresh();
              toast('本集简报已保存为新版本');
              close();
            })
            .catch((e) => setError((e as Error).message))
            .finally(() => setBusy(false));
        }}
      >
        <DesignButton
          kind="episode_brief"
          episodeId={episode.id}
          disabled={busy || (!!existing && versions.isPending)}
          currentDraft={{
            ...extra,
            episodeId: episode.id,
            goal,
            openingHook: opening,
            endingHook: ending,
            targetSeconds: episode.targetFormat.targetSeconds,
            plannedStateChanges: lines(changes),
            unresolvedQuestions: lines(questions),
          }}
          onAdopt={(c) => {
            setGoal(String(c.goal));
            setOpening(String(c.openingHook));
            setEnding(String(c.endingHook));
            setChanges((c.plannedStateChanges as string[]).join('\n'));
            setQuestions((c.unresolvedQuestions as string[]).join('\n'));
            setExtra(c);
          }}
        />
        <Field label="本集推进什么？">
          <textarea
            required
            rows={3}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="人物要达成什么，遇到什么阻力？"
          />
        </Field>
        <Field label="如何开场">
          <textarea
            required
            rows={2}
            value={opening}
            onChange={(e) => setOpening(e.target.value)}
            placeholder="接住上集的动作，或用一个画面让观众留下。"
          />
        </Field>
        <Field label="结尾留下什么">
          <textarea
            required
            rows={2}
            value={ending}
            onChange={(e) => setEnding(e.target.value)}
            placeholder="发生了什么变化，下一集从哪里继续？"
          />
        </Field>
        <Field label="计划中的变化 · 每行一项">
          <textarea rows={3} value={changes} onChange={(e) => setChanges(e.target.value)} />
        </Field>
        <Field label="留到后集的问题 · 每行一项">
          <textarea rows={3} value={questions} onChange={(e) => setQuestions(e.target.value)} />
        </Field>
        {episode.continuityBaseline.dependencyValidity === 'provisional' && (
          <div className="info-box amber">
            上集尚未定稿。这份简报属于预制作，不会自动成为正式剧情。
          </div>
        )}
        {error && <p className="form-error">{error}</p>}
        <footer className="dialog-footer">
          <button type="button" className="button secondary" onClick={close}>
            关闭
          </button>
          <button className="button primary" disabled={busy}>
            {busy ? (
              <Spinner />
            ) : (
              <>
                <Save size={16} />
                保存简报
              </>
            )}
          </button>
        </footer>
      </form>
    </Dialog>
  );
}
export function CharactersPage() {
  const { base, project, branch, go } = useStudio();
  const characters = useApi<Page<Schema<'Character'>>>(
    `${base}/series/${project?.id ?? ''}/characters`,
    !!project,
  );
  const artifacts = useArtifacts('character_profile');
  const images = useTasks(`${base}/studio-tasks?seriesId=${project?.id}&branchId=${branch?.id}`);
  const [create, setCreate] = useState(false),
    [selected, setSelected] = useState<Schema<'Character'> | null>(null);
  if (!project) return <MissingProject />;
  const rows = characters.data?.items ?? [];
  return (
    <div className="page">
      {heading(
        'CHARACTERS THAT STAY WITH YOU',
        '先认识他，再让他出现。',
        '先建立文字人设，再生成角色图片。候选与生成记录会保留，方便你逐次审阅。',
        <button className="button primary" onClick={() => setCreate(true)}>
          <Plus size={17} />
          新建角色
        </button>,
      )}
      <div className="character-guidance">
        <span className="guidance-number">01</span>
        <div>
          <strong>从文字人设开始</strong>
          <p>想要什么、害怕什么、不愿跨过什么底线。形象与声音会在此基础上展开。</p>
        </div>
        <BookOpen size={22} />
      </div>
      {rows.length === 0 ? (
        <Empty
          icon={<UsersRound size={30} />}
          title="谁会走进你的故事？"
          text="主角、搭档或对手。从一个名字开始，为他们建立可复用的人设。"
          action={
            <button className="button secondary" onClick={() => setCreate(true)}>
              创建第一个角色 <Plus size={16} />
            </button>
          }
        />
      ) : (
        <div className="character-grid">
          {rows.map((c, i) => {
            const image = images.data?.items.find(
              (t) => t.kind === 'image' && t.characterId === c.id && t.state === 'completed',
            )?.result.asset;
            return (
              <button key={c.id} className="character-card" onClick={() => setSelected(c)}>
                <div className={`character-portrait palette-${i % 3}`}>
                  {image ? (
                    <img src={image.url} alt={`${c.displayName}的图片候选`} />
                  ) : (
                    <>
                      <div className="portrait-lines" />
                      <span>{c.displayName.slice(0, 1)}</span>
                    </>
                  )}
                  <Badge>{image ? '图片候选' : '形象待设计'}</Badge>
                </div>
                <div className="character-info">
                  <h3>
                    {c.displayName}
                    <ArrowUpRight size={17} />
                  </h3>
                  <p>
                    {artifacts.data?.items.some((a) => a.characterId === c.id)
                      ? '已有文字人设草稿'
                      : '文字人设待完善'}
                  </p>
                  <div className="character-phases">
                    <span>
                      <BookOpen size={13} />
                      文字
                    </span>
                    <span>形象</span>
                    <span>声线</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {create && (
        <CreateCharacter
          close={() => setCreate(false)}
          created={(c) => {
            setCreate(false);
            setSelected(c);
          }}
        />
      )}
      {selected && <CharacterEditor character={selected} close={() => setSelected(null)} />}
    </div>
  );
}
function CreateCharacter({
  close,
  created,
}: {
  close: () => void;
  created: (c: Schema<'Character'>) => void;
}) {
  const { base, project, refresh } = useStudio();
  const [name, setName] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  return (
    <Dialog title="让一个角色登场" subtitle="先给他一个名字，再慢慢认识他。" close={close}>
      <form
        className="dialog-body"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void api<Schema<'Character'>>(`${base}/series/${project?.id}/characters`, 'POST', {
            displayName: name.trim(),
          })
            .then(async (c) => {
              await refresh();
              created(c);
            })
            .catch((e) => setError((e as Error).message))
            .finally(() => setBusy(false));
        }}
      >
        <Field label="角色名称">
          <input
            autoFocus
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：阿橘"
            maxLength={100}
          />
        </Field>
        {error && <p className="form-error">{error}</p>}
        <footer className="dialog-footer">
          <button className="button secondary" type="button" onClick={close}>
            取消
          </button>
          <button className="button primary" disabled={busy || !name.trim()}>
            {busy ? <Spinner /> : '创建角色'}
          </button>
        </footer>
      </form>
    </Dialog>
  );
}
const profileFields = [
  ['dramaticRole', '在故事中的位置', '主角、搭档、对手，或者意想不到的人。'],
  ['desire', '他真正想要什么', '让他不断往前走的欲望。'],
  ['fear', '他害怕什么', '不愿失去的东西，或者不敢面对的事。'],
  ['flaw', '他的缺点', '一个会让选择变难的弱点。'],
  ['secret', '他的秘密', '也可以明确写“没有隐藏秘密”。'],
  ['speakingStyle', '他说话的方式', '克制、直接、嘴硬，还是总爱开玩笑。'],
  ['growthPlan', '他会如何改变', '这是成长计划，还不是已经发生的事实。'],
];
function CharacterEditor({
  character,
  close,
}: {
  character: Schema<'Character'>;
  close: () => void;
}) {
  const { base, project, branch, baselines, refresh, toast } = useStudio();
  const artifacts = useArtifacts('character_profile');
  const existing = artifacts.data?.items.find((a) => a.characterId === character.id);
  const versions = useApi<Page<Version>>(
    `${base}/artifacts/${existing?.id ?? ''}/versions`,
    !!existing,
  );
  const latest =
    versions.data?.items.find((v) => v.branchId === branch?.id) ??
    baselines.find((v) => v.artifactId === existing?.id);
  const [values, setValues] = useState<Record<string, string>>({}),
    [extra, setExtra] = useState<Record<string, unknown>>({}),
    [traits, setTraits] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [review, setReview] = useState<Version | null>(null);
  useEffect(() => {
    if (latest) {
      const v: Record<string, string> = {};
      for (const [key] of profileFields) if (key) v[key] = String(latest.content[key] ?? '');
      setValues(v);
      setExtra(latest.content);
      setTraits(((latest.content.immutableTraits as string[]) ?? []).join('\n'));
    }
  }, [latest?.id]);
  return (
    <>
      <Dialog
        title={`${character.displayName} · 角色详情`}
        subtitle="角色图片会自动保存；可查看生成记录、下载原图，也可继续完善文字人设。"
        close={close}
      >
        <form
          className="dialog-body"
          onSubmit={(e) => {
            e.preventDefault();
            if (!project || !branch) return;
            setBusy(true);
            setError('');
            void saveVersion(
              base,
              project.id,
              branch.id,
              'character_profile',
              `${character.displayName}的文字人设`,
              {
                ...extra,
                characterId: character.id,
                name: character.displayName,
                ...values,
                immutableTraits: lines(traits),
                relationships: extra.relationships ?? [],
              },
              existing,
              character.id,
            )
              .then(async (v) => {
                await refresh();
                toast('人设已保存为新草稿');
                setReview(v);
              })
              .catch((e) => setError((e as Error).message))
              .finally(() => setBusy(false));
          }}
        >
          <CharacterImagesPanel
            character={character}
            currentDraft={{ ...extra, ...values, immutableTraits: lines(traits) }}
            disabled={busy || (!!existing && versions.isPending)}
          />
          <CharacterVoicePanel key={character.id} characterId={character.id} />
          <DesignButton
            kind="character_profile"
            characterId={character.id}
            disabled={busy || (!!existing && versions.isPending)}
            currentDraft={{
              ...extra,
              ...values,
              characterId: character.id,
              name: character.displayName,
              immutableTraits: lines(traits),
              relationships: extra.relationships ?? [],
            }}
            onAdopt={(c) => {
              setValues(
                Object.fromEntries(profileFields.map(([key = '']) => [key, String(c[key] ?? '')])),
              );
              setTraits((c.immutableTraits as string[]).join('\n'));
              setExtra(c);
            }}
          />
          <div className="form-grid">
            {profileFields.map(([key = '', label = '', placeholder = '']) => (
              <Field key={key} label={label}>
                <textarea
                  rows={2}
                  required
                  value={values[key] ?? ''}
                  onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                  placeholder={placeholder}
                />
              </Field>
            ))}
          </div>
          <Field label="不变特征 · 每行一项">
            <textarea rows={3} value={traits} onChange={(e) => setTraits(e.target.value)} />
          </Field>
          {Array.isArray(extra.relationships) && extra.relationships.length > 0 && (
            <div className="info-box">
              已有人物关系保留：
              {extra.relationships.map((r: { relationship: string }) => r.relationship).join('；')}
            </div>
          )}
          {latest && (
            <div className="version-meta">
              <Badge tone={latest.approvalStatus === 'approved' ? 'green' : 'neutral'}>
                {labels[latest.approvalStatus] ?? latest.approvalStatus}
              </Badge>
              <span>每次保存都会保留旧版本</span>
            </div>
          )}
          {error && <p className="form-error">{error}</p>}
          <footer className="dialog-footer">
            {latest?.approvalStatus === 'draft' && (
              <button type="button" className="button secondary" onClick={() => setReview(latest)}>
                审阅并定稿
              </button>
            )}
            <button className="button primary" disabled={busy}>
              {busy ? (
                <Spinner />
              ) : (
                <>
                  <Save size={16} />
                  保存新草稿
                </>
              )}
            </button>
          </footer>
        </form>
      </Dialog>
      {review && (
        <ApprovalDialog
          version={review}
          purpose="profile"
          close={() => setReview(null)}
          done={() => {
            setReview(null);
            close();
          }}
        />
      )}
    </>
  );
}
const bibleFields = [
  ['logline', '一句话故事', '谁，因为什么，必须做一件什么事？'],
  ['theme', '这个故事真正想讲什么', '藏在情节背后的情感或问题。'],
  ['seasonArc', '系列主线', '从哪里开始，冲突如何一步步升级？'],
  ['endingPlan', '计划中的终点', '可以写一个方向，暂时不必锁死每个细节。'],
];
export function StoryPage() {
  const { base, project, branch, baselines, perform, busy, go } = useStudio();
  const artifacts = useArtifacts('story_bible');
  const artifact = artifacts.data?.items[0];
  const versions = useApi<Page<Version>>(
    `${base}/artifacts/${artifact?.id ?? ''}/versions`,
    !!artifact,
  );
  const latest =
    versions.data?.items.find((v) => v.branchId === branch?.id) ??
    baselines.find((v) => v.artifactId === artifact?.id);
  const [values, setValues] = useState<Record<string, string>>({}),
    [rules, setRules] = useState(''),
    [ruleMeta, setRuleMeta] = useState<{ id: string; statement: string; sourceRefs: unknown[] }[]>(
      [],
    ),
    [questions, setQuestions] = useState(''),
    [review, setReview] = useState<Version | null>(null),
    [history, setHistory] = useState(false);
  useEffect(() => {
    if (latest) {
      const v: Record<string, string> = {};
      for (const [key] of bibleFields) if (key) v[key] = String(latest.content[key] ?? '');
      setValues(v);
      setRuleMeta(
        (latest.content.worldRules as { id: string; statement: string; sourceRefs: unknown[] }[]) ??
          [],
      );
      setRules(
        Array.isArray(latest.content.worldRules)
          ? latest.content.worldRules.map((r: { statement: string }) => r.statement).join('\n')
          : '',
      );
      setQuestions(
        Array.isArray(latest.content.openQuestions) ? latest.content.openQuestions.join('\n') : '',
      );
    }
  }, [latest?.id]);
  if (!project || !branch) return <MissingProject />;
  return (
    <div className="page story-page">
      {heading(
        'THE WORLD BEHIND YOUR STORY',
        '故事会变，来处不丢。',
        '世界规则、主题与主线集中保存，后续每集都能找到准确的依据。',
        <div className="heading-actions">
          <DesignButton
            kind="story_bible"
            disabled={busy || (!!artifact && versions.isPending)}
            currentDraft={{ ...values, worldRules: lines(rules), openQuestions: lines(questions) }}
            onAdopt={(c) => {
              setValues(
                Object.fromEntries(bibleFields.map(([key = '']) => [key, String(c[key] ?? '')])),
              );
              const next = c.worldRules as typeof ruleMeta;
              setRules(next.map((r) => r.statement).join('\n'));
              setRuleMeta(
                next.map((r) => ruleMeta.find((old) => old.statement === r.statement) ?? r),
              );
              setQuestions((c.openQuestions as string[]).join('\n'));
            }}
          />
          <button className="button secondary" onClick={() => setHistory(true)}>
            <History size={16} />
            版本记录
          </button>
        </div>,
      )}
      <div className="document-header">
        <div>
          <BookOpen size={22} />
          <div>
            <strong>{project.title} · 故事圣经</strong>
            <span>
              {branch.name} ·{' '}
              {latest ? `版本 ${versions.data?.items.length ?? 1}` : '等待第一份草稿'}
            </span>
          </div>
        </div>
        {latest ? (
          <Badge tone={latest.approvalStatus === 'approved' ? 'green' : 'amber'}>
            {labels[latest.approvalStatus]}
          </Badge>
        ) : (
          <Badge>未编写</Badge>
        )}
      </div>
      <form
        className="bible-form"
        onSubmit={(e) => {
          e.preventDefault();
          void perform(async () => {
            const content = {
              ...values,
              worldRules: rules
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean)
                .map(
                  (statement) =>
                    ruleMeta.find((r) => r.statement === statement) ?? {
                      id: crypto.randomUUID(),
                      statement,
                      sourceRefs: [],
                    },
                ),
              openQuestions: questions
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
            };
            const saved = await saveVersion(
              base,
              project.id,
              branch.id,
              'story_bible',
              `${project.title}的故事圣经`,
              content,
              artifact,
            );
            setReview(saved);
          }, '故事草稿已保存');
        }}
      >
        <div className="bible-main">
          {bibleFields.map(([key = '', label = '', placeholder = ''], i) => (
            <div className="bible-section" key={key}>
              <span className="section-num">0{i + 1}</span>
              <Field label={label}>
                <textarea
                  rows={i === 0 ? 2 : 3}
                  required
                  value={values[key] ?? ''}
                  onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                  placeholder={placeholder}
                />
              </Field>
            </div>
          ))}
        </div>
        <aside className="bible-side">
          <div className="aside-card">
            <h3>世界的边界</h3>
            <p>每行一条。让剧情可以惊喜，但不自相矛盾。</p>
            <textarea
              aria-label="世界规则"
              rows={6}
              value={rules}
              onChange={(e) => setRules(e.target.value)}
              placeholder="例如：信件只能送往过去，不能改变已经发生的事实。"
            />
          </div>
          <div className="aside-card">
            <h3>还想讨论的问题</h3>
            <p>留白可以保留，不需要假装已有答案。</p>
            <textarea
              aria-label="未决问题"
              rows={4}
              value={questions}
              onChange={(e) => setQuestions(e.target.value)}
              placeholder="每行记录一个问题。"
            />
          </div>
          <div className="tip-line">
            <ShieldCheck size={17} />
            未来剧情计划不会被当成已发生事实。
          </div>
        </aside>
        <footer className="document-actions">
          <span>
            <Save size={14} /> 保存创建新版本，不覆盖历史定稿
          </span>
          <div>
            {latest?.approvalStatus === 'draft' && (
              <button className="button secondary" type="button" onClick={() => setReview(latest)}>
                审阅并定稿
              </button>
            )}
            <button className="button primary" disabled={busy}>
              {busy ? (
                <Spinner />
              ) : (
                <>
                  <Save size={16} />
                  保存草稿
                </>
              )}
            </button>
          </div>
        </footer>
      </form>
      {review && (
        <ApprovalDialog
          version={review}
          purpose="story_baseline"
          close={() => setReview(null)}
          done={() => setReview(null)}
        />
      )}{' '}
      {history && (
        <Dialog
          title="故事的每一次变化"
          subtitle="旧版本与定稿记录会保留，可随时回看。"
          close={() => setHistory(false)}
        >
          <div className="dialog-body">
            {versions.data?.items.length ? (
              versions.data.items.map((v, i) => (
                <button
                  className="history-row"
                  key={v.id}
                  onClick={() => {
                    setHistory(false);
                    setReview(v);
                  }}
                >
                  <span>版本 {versions.data.items.length - i}</span>
                  <Badge tone={v.approvalStatus === 'approved' ? 'green' : 'neutral'}>
                    {labels[v.approvalStatus]}
                  </Badge>
                  <span>{new Date(v.createdAt).toLocaleString('zh-CN')}</span>
                  <ChevronRight size={16} />
                </button>
              ))
            ) : (
              <Empty title="还没有版本记录" text="保存第一份草稿后，每次修改都会留下记录。" />
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}
const contentLabels: Record<string, string> = {
  logline: '一句话故事',
  theme: '主题',
  seasonArc: '系列主线',
  endingPlan: '计划中的终点',
  worldRules: '世界规则',
  openQuestions: '待讨论',
  name: '角色',
  dramaticRole: '角色位置',
  desire: '欲望',
  fear: '恐惧',
  flaw: '缺点',
  secret: '秘密',
  speakingStyle: '说话方式',
  growthPlan: '成长计划',
  immutableTraits: '固定特征',
  relationships: '关系',
};
function ApprovalDialog({
  version,
  purpose,
  close,
  done,
}: {
  version: Version;
  purpose: Schema<'ApprovalScope'>['purpose'];
  close: () => void;
  done: () => void;
}) {
  const { base, project, branch, perform, busy } = useStudio();
  const [accepted, setAccepted] = useState(false);
  return (
    <Dialog
      title={version.approvalStatus === 'approved' ? '已定稿版本' : '把这一版，确认为创作依据'}
      subtitle="请审阅具体内容。定稿后，后续创作会引用这个准确版本。"
      close={close}
    >
      <div className="dialog-body">
        <div className="approval-content">
          {Object.entries(version.content)
            .filter(
              ([key, value]) => contentLabels[key] && (!Array.isArray(value) || value.length > 0),
            )
            .map(([key, value]) => (
              <section key={key}>
                <h4>{contentLabels[key]}</h4>
                <p>
                  {Array.isArray(value)
                    ? value
                        .map((v: unknown) =>
                          typeof v === 'string'
                            ? v
                            : typeof v === 'object' && v !== null && 'statement' in v
                              ? String(v.statement)
                              : '关系记录',
                        )
                        .join('\n')
                    : String(value)}
                </p>
              </section>
            ))}
        </div>
        <div className="version-meta">
          <Badge>{labels[version.kind] ?? version.kind}</Badge>
          <span>
            版本 {version.id.slice(0, 8)} · 依赖 {labels[version.dependencyValidity]}
          </span>
        </div>
        {version.approvalStatus !== 'approved' && (
          <>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              <span>我已审阅，确认将这一版作为后续创作依据。</span>
            </label>
            <footer className="dialog-footer">
              <button className="button secondary" onClick={close}>
                继续修改
              </button>
              <button
                className="button primary"
                disabled={!accepted || busy}
                onClick={() =>
                  void perform(async () => {
                    await api(`${base}/approvals`, 'POST', {
                      exactVersionId: version.id,
                      expectedRevision: version.revision,
                      contentHash: version.contentHash,
                      dependencySnapshotHash: version.dependencySnapshotHash,
                      scope: { seriesId: project!.id, branchId: branch!.id, purpose },
                    } satisfies Schema<'ApprovalCreate'>);
                    done();
                  }, '这一版已正式定稿')
                }
              >
                <CheckCheck size={16} />
                确认定稿
              </button>
            </footer>
          </>
        )}
      </div>
    </Dialog>
  );
}
export function MemoryPage() {
  const { base, project, branch, go } = useStudio();
  const memory = useApi<Page<Schema<'MemoryItem'>>>(
    `${base}/memory?seriesId=${project?.id ?? ''}&branchId=${branch?.id ?? ''}&asOfStoryPosition=0`,
    !!project && !!branch,
  );
  if (!project) return <MissingProject />;
  const items = memory.data?.items ?? [];
  return (
    <div className="page">
      {heading(
        'NOTES THAT KEEP YOUR WORLD TOGETHER',
        '让故事记得自己。',
        '设定、已发生事实和未来计划分开记录。每一条，都有可追溯的来处。',
      )}
      <div className="memory-summary">
        <div>
          <BookOpen size={21} />
          <strong>系列规范</strong>
          <span>{items.filter((i) => i.category === 'bible').length} 条已确认</span>
        </div>
        <div>
          <Clapperboard size={21} />
          <strong>正式剧情</strong>
          <span>按已发布剧集留存</span>
        </div>
        <div>
          <Brain size={21} />
          <strong>伏笔与知情</strong>
          <span>分别保存在每集交接中</span>
        </div>
      </div>
      <ContinuityLedger />
      {items.length === 0 ? (
        <Empty
          icon={<Brain size={30} />}
          title="记忆，从你的第一次确认开始"
          text="聊天设想不会自动变成事实。先确认故事或人设，这里会保存它们的准确来源。"
          action={
            <button className="button secondary" onClick={() => go('story')}>
              完善故事圣经 <ArrowRight size={16} />
            </button>
          }
        />
      ) : (
        <div className="memory-list">
          {items.map((item) => {
            const separator = item.statement.indexOf(' · ');
            let content: Record<string, unknown> = {};
            try {
              content = JSON.parse(item.statement.slice(separator + 3)) as Record<string, unknown>;
            } catch {}
            return (
              <article className="memory-card" key={item.id}>
                <div>
                  <Badge tone="green">
                    已确认 · {item.category === 'plan' ? '未来计划' : '系列规范'}
                  </Badge>
                  <span className="quiet">有来源的正式版本</span>
                </div>
                <h3>{separator > 0 ? item.statement.slice(0, separator) : '创作记录'}</h3>
                <p>
                  {String(
                    content.logline ??
                      content.desire ??
                      content.theme ??
                      '已确认内容，可在原对象中查看。',
                  )}
                </p>
                <button
                  className="text-button"
                  onClick={() => go(content.characterId ? 'characters' : 'story')}
                >
                  <Link2 size={14} />
                  查看来源版本 <ArrowUpRight size={14} />
                </button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
export function ModelsPage() {
  const { base, projects, workspaceId, perform, busy } = useStudio();
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const scopeTitle = projects.find((item) => item.id === seriesId)?.title ?? '当前系列';
  const models = useApi<Page<Model>>(`${base}/models`),
    connections = useApi<Page<Schema<'Connection'>>>(`${base}/connections`);
  const policy = useApi<Schema<'ModelPolicy'>>(modelPolicyPath(base, seriesId));
  const [family, setFamily] = useState('all'),
    [tab, setTab] = useState<'catalog' | 'roles'>('catalog');
  const [connectionId, setConnectionId] = useState('all');
  const [kind, setKind] = useState('all');
  const rows = (models.data?.items ?? []).filter(
    (m) =>
      (family === 'all' || m.family === family) &&
      (connectionId === 'all' || m.connectionId === connectionId) &&
      (kind === 'all' || modelKind(m) === kind),
  );
  const families = [
    ['all', '全部模型'],
    ['claude', 'Claude'],
    ['gpt', 'GPT'],
    ['grok', 'Grok'],
    ['gemini', 'Gemini'],
    ['glm', 'GLM'],
    ['deepseek', 'DeepSeek'],
  ];
  return (
    <div className="page models-page">
      {heading(
        'THE RIGHT MODEL FOR EVERY PART',
        '让合适的模型，参与合适的创作。',
        '聊天、编剧、人设和媒体各自选择模型。切换模型不会清空故事记忆。',
      )}
      <div className="info-box">
        <Field
          label="创作模型偏好的作用范围"
          hint={
            seriesId
              ? `当前只修改「${scopeTitle}」的模型分工。连接与模型目录仍由整个工作区共享。`
              : '工作区默认用于未单独指定模型的系列；不会改写各系列已有的偏好。'
          }
        >
          <select
            value={seriesId ?? ''}
            onChange={(event) =>
              navigate(
                event.target.value
                  ? `/w/${workspaceId}/series/${event.target.value}/models`
                  : `/w/${workspaceId}/models`,
              )
            }
          >
            <option value="">工作区默认</option>
            {projects.map((item) => (
              <option value={item.id} key={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="connection-grid">
        {connections.data?.items.map((c) => (
          <article className="connection-card" key={c.id}>
            <div className={`connection-icon ${c.health === 'ready' ? 'ready' : ''}`}>
              <Link2 size={19} />
            </div>
            <div>
              <strong>{c.displayName}</strong>
              <p>
                {c.health === 'ready'
                  ? '服务端凭据已配置'
                  : c.health === 'unconfigured'
                    ? '等待服务端配置'
                    : c.health}
              </p>
            </div>
            <ConnectionEditor connection={c} />
            <button
              className="icon-button"
              title="读取真实模型列表，不触发生成"
              aria-label={`刷新 ${c.displayName} 模型列表`}
              disabled={busy || c.health === 'unconfigured'}
              onClick={() =>
                void perform(
                  () =>
                    api(`${base}/connections/${c.id}/discover`, 'POST', {
                      expectedRevision: c.revision,
                    }),
                  '模型列表已刷新；生成能力仍需单独验证',
                )
              }
            >
              <RefreshCw size={16} />
            </button>
          </article>
        ))}
      </div>
      <div className="tabs">
        <button className={tab === 'catalog' ? 'active' : ''} onClick={() => setTab('catalog')}>
          模型目录 <span>{models.data?.items.length ?? 0}</span>
        </button>
        <button className={tab === 'roles' ? 'active' : ''} onClick={() => setTab('roles')}>
          创作角色分工
        </button>
      </div>
      {tab === 'catalog' ? (
        <>
          <div className="filter-row">
            <select
              aria-label="按模型用途筛选"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="all">全部用途</option>
              <option value="text">文字</option>
              <option value="image">图片</option>
              <option value="video">视频</option>
              <option value="voice">语音配音</option>
              <option value="music">音乐</option>
            </select>
            <select
              aria-label="按模型服务筛选"
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
            >
              <option value="all">全部模型服务</option>
              {connections.data?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
            {families.map(([key = '', label = '']) => (
              <button
                className={family === key ? 'active' : ''}
                key={key}
                onClick={() => setFamily(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="info-box">
            <ShieldCheck size={18} />
            <span>
              同名模型按服务分别配置、测试和保存结果。模型名称后的来源可区分 Grok2API 与
              CLIProxyAPI；生成成功并保存素材后才标记为已验证。
            </span>
          </div>
          <div className="model-table">
            <div className="table-head">
              <span>模型</span>
              <span>能力证据</span>
              <span>连接与可用状态</span>
            </div>
            {rows.map((m) => (
              <div className="model-row" key={m.catalogModelId}>
                <div>
                  <span className={`model-symbol ${m.family}`}>
                    {m.family.slice(0, 1).toUpperCase()}
                  </span>
                  <span>
                    <strong>{m.displayName}</strong>
                    <small>
                      {families.find((f) => f[0] === m.family)?.[1] ?? m.family} ·{' '}
                      {
                        {
                          text: '文字',
                          image: '图片',
                          video: '视频',
                          voice: '语音配音',
                          music: '音乐',
                        }[modelKind(m)]
                      }
                    </small>
                  </span>
                </div>
                <div>
                  <Badge tone={m.selectable ? 'green' : 'neutral'}>
                    {m.selectable
                      ? '已验证'
                      : m.capabilities.some((c) => c.evidenceState === 'verified')
                        ? '已有验证记录，需启用'
                        : '待验证'}
                  </Badge>
                </div>
                <div>
                  <span className="model-status-dot" />
                  {m.blockerReasons.map((r) => labels[r] ?? r).join(' · ') || '可用'}
                  <ModelSetupButton model={m} />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="role-grid">
          {(
            [
              'chat',
              'planner',
              'writer',
              'character_designer',
              'storyboard_designer',
              'continuity_reviewer',
              'qa_reviewer',
              'image',
              'music',
              'video',
            ] as const
          ).map((role) => (
            <article className="role-card" key={role}>
              <div>
                <SlidersHorizontal size={18} />
                <h3>{labels[role]}</h3>
              </div>
              <select
                aria-label={`${labels[role]}模型`}
                value={policy.data?.bindings[role]?.primary.catalogModelId ?? ''}
                onChange={(e) => {
                  const m = models.data?.items.find((m) => m.catalogModelId === e.target.value);
                  if (!m || !policy.data) return;
                  void perform(
                    () =>
                      api(modelPolicyPath(base, seriesId), 'PUT', {
                        expectedRevision: policy.data.revision,
                        bindings: {
                          [role]: {
                            primary: {
                              catalogModelId: m.catalogModelId,
                              modelRevision: m.revision,
                              role,
                              allowFallback: false,
                            },
                            fallbacks: [],
                            maxAttempts: 1,
                          },
                        },
                      }),
                    '模型偏好已保存，仅用于后续新任务',
                  );
                }}
              >
                <option value="">选择默认模型</option>
                {models.data?.items
                  .filter((m) =>
                    ['image', 'music', 'video'].includes(role)
                      ? m.upstreamModelId.includes(role)
                      : isTextModel(m),
                  )
                  .map((m) => (
                    <option key={m.catalogModelId} value={m.catalogModelId}>
                      {m.displayName}
                      {m.selectable ? '' : ' · 待验证'}
                    </option>
                  ))}
              </select>
              <small>保存偏好会用于后续新任务。</small>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
export function JobsPage() {
  const { base, go } = useStudio();
  const jobs = useApi<Page<Schema<'Job'>>>(`${base}/jobs`);
  return (
    <div className="page">
      {heading(
        'EVERY STEP, ACCOUNTED FOR',
        '制作可以暂停，进度不会丢。',
        '任务与输入版本会保存在后端。离开页面不等于取消，恢复也不会重新收费。',
      )}
      <ProductionTaskList />
      <div className="jobs-top">
        <span>
          <CirclePlay size={18} />
          全部任务
        </span>
        <Badge>{jobs.data?.items.length ?? 0} 个</Badge>
        <button className="text-button" onClick={() => void jobs.refetch()}>
          <RefreshCw size={14} />
          刷新状态
        </button>
      </div>
      {!jobs.data?.items.length ? (
        <Empty
          icon={<Film size={30} />}
          title="让创作先有一个方向"
          text="准备好故事与人物、确认具体生成计划后，任务会出现在这里。"
          action={
            <button className="button secondary" onClick={() => go('studio')}>
              回到创作工作台 <ArrowRight size={16} />
            </button>
          }
        />
      ) : (
        jobs.data.items.map((job) => (
          <div className="grant-row" key={job.id}>
            <div>
              <strong>
                {job.kind === 'text'
                  ? '创作讨论'
                  : job.kind === 'model_probe'
                    ? '模型探测'
                    : job.kind}
              </strong>
              <p>{job.modelSnapshot?.upstreamModelId}</p>
            </div>
            <Badge
              tone={
                job.state === 'completed'
                  ? 'green'
                  : job.state === 'outcome_unknown'
                    ? 'amber'
                    : 'neutral'
              }
            >
              {(
                {
                  queued: '等待执行',
                  submitted: '已提交',
                  completed: '已完成',
                  failed: '未完成',
                  outcome_unknown: '结果待核对',
                  running: '执行中',
                } as Record<string, string>
              )[job.state] ?? job.state}
            </Badge>
            <span>
              {job.errorCode === 'OUTCOME_UNKNOWN'
                ? '调用结果未知，已保留额度，未自动重试。'
                : (job.errorCode ?? job.blockerReasons.join(' · '))}
            </span>
          </div>
        ))
      )}
    </div>
  );
}
function MissingProject() {
  const { go } = useStudio();
  return (
    <div className="page">
      <Empty
        title="先选一个要继续的故事"
        text="你的角色、剧集和记忆会跟随各自的系列保存。"
        action={
          <button className="button primary" onClick={() => go('projects')}>
            查看系列 <ArrowRight size={16} />
          </button>
        }
      />
    </div>
  );
}
